import { useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";

/**
 * Section that is collapsible on mobile (<md). On desktop, behavior depends on
 * `forceOpen` (always-open) or `controlledOpen` (parent-controlled via Expand
 * all / Collapse all). When `controlledOpen` is undefined, desktop is always
 * open.
 */
export default function CollapsibleSection({
  id,
  title,
  overline,
  rightSlot,
  defaultOpen = false,
  forceOpen = false,
  controlledOpen,
  children,
  testId,
  badge,
}) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );
  const [open, setOpen] = useState(defaultOpen || !isMobile);

  useEffect(() => {
    const onResize = () => {
      const m = window.innerWidth < 768;
      setIsMobile(m);
      if (!m && controlledOpen === undefined) setOpen(true);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [controlledOpen]);

  // Sync to controlledOpen prop when parent toggles expand/collapse all.
  useEffect(() => {
    if (controlledOpen !== undefined) setOpen(Boolean(controlledOpen));
  }, [controlledOpen]);

  const headerOpen = forceOpen ? true : open;

  return (
    <section
      id={id}
      className="cre-fade-up scroll-mt-20"
      data-testid={testId}
      aria-expanded={headerOpen}
    >
      <button
        type="button"
        onClick={() => !forceOpen && setOpen((o) => !o)}
        className={`w-full flex items-center justify-between gap-3 mb-3 text-left ${
          forceOpen ? "cursor-default" : "cursor-pointer"
        }`}
        data-testid={testId ? `${testId}-toggle` : undefined}
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {overline ? <span className="cre-overline">{overline}</span> : null}
          {title ? (
            <h2 className="font-heading text-lg md:text-xl font-bold tracking-tight truncate">
              {title}
            </h2>
          ) : null}
          {badge}
          <span className="hidden md:block h-px flex-1 bg-white/10 ml-2" />
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {rightSlot}
          {!forceOpen ? (
            <ChevronDown
              className={`h-4 w-4 text-zinc-400 transition-transform ${
                headerOpen ? "rotate-180" : ""
              }`}
              strokeWidth={1.75}
            />
          ) : null}
        </div>
      </button>
      <div className={`${!forceOpen && !headerOpen ? "hidden" : "block"}`}>
        {children}
      </div>
    </section>
  );
}
